import { useCallback, useEffect, useRef, useState } from "react";
import { useCachedQuery } from "./useCachedQuery";
import { getCsrfHeader } from "../lib/csrf";
import type { ToolPermission, ToolPermissionsView } from "../types/integration";

export type ToolPermissionsStatus = "loading" | "ready" | "error";

export type UseToolPermissionsResult = {
  status: ToolPermissionsStatus;
  data: ToolPermissionsView | null;
  error: string | null;
  saving: boolean;
  // toolSlug null = the integration-level default rule.
  setPermission: (toolSlug: string | null, permission: ToolPermission) => Promise<void>;
  clearPermission: (toolSlug: string) => Promise<void>;
  resetAll: () => Promise<void>;
};

// Pure cores, exported for unit testing without a React renderer. Gating: we
// only fetch with a tenant and slug; the API decides rollout eligibility.
export const canFetchToolPermissions = (
  enabled: boolean,
  tenantId: string | null,
  slug: string,
): boolean => enabled && tenantId !== null && slug !== "";

export const toolPermissionsBasePath = (tenantId: string, slug: string): string =>
  `/api/integrations/${encodeURIComponent(tenantId)}/${encodeURIComponent(slug)}/tool-permissions`;

// The useCachedQuery cache resource key — one localStorage slot per tenant+slug.
export const toolPermissionsCacheResource = (tenantId: string, slug: string): string =>
  `tool-permissions:${tenantId}:${slug}`;

// How long the "couldn't save" banner stays up before auto-dismissing.
const MUTATION_ERROR_DISMISS_MS = 6_000;

export const useToolPermissions = (
  tenantId: string | null,
  slug: string,
  enabled: boolean,
): UseToolPermissionsResult => {
  const canFetch = canFetchToolPermissions(enabled, tenantId, slug);

  // Bind cached responses to their tenant and integration across route changes.
  const resource = tenantId === null ? "tool-permissions:disabled" : toolPermissionsCacheResource(tenantId, slug);
  const currentResource = useRef(resource);
  currentResource.current = resource;
  const query = useCachedQuery<{ resource: string; view: ToolPermissionsView | null }>({
    cacheResource: canFetch && tenantId !== null ? toolPermissionsCacheResource(tenantId, slug) : null,
    queryKey: canFetch && tenantId !== null ? toolPermissionsBasePath(tenantId, slug) : "tool-permissions:disabled",
    fetcher: async (signal) => {
      if (!canFetch || tenantId === null) return { resource, view: null };
      const res = await fetch(toolPermissionsBasePath(tenantId, slug), { credentials: "include", signal });
      if (res.status === 204 || res.status === 404) return { resource, view: null };
      if (!res.ok) throw new Error(`tool permissions fetch failed (${res.status})`);
      return { resource, view: (await res.json()) as ToolPermissionsView };
    },
  });

  const [saving, setSaving] = useState<boolean>(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  // The save-error banner auto-dismisses: the failed write immediately resyncs
  // the toggles to server truth (below), so after a short read window the
  // message is stale — without this it lingered until the NEXT mutation.
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flagMutationError = useCallback((message: string): void => {
    setMutationError(message);
    if (errorTimerRef.current !== null) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setMutationError(null), MUTATION_ERROR_DISMISS_MS);
  }, []);
  useEffect(
    () => () => {
      if (errorTimerRef.current !== null) clearTimeout(errorTimerRef.current);
    },
    [],
  );

  // useCachedQuery returns a fresh object each render but its refetch/mutate are
  // stable — depend on those directly so setPermission/clearPermission (and the
  // parent's controls memo over an 800-tool list) don't churn every render.
  const { refetch, mutate: writeThroughCache } = query;

  // The success-writethrough vs failed-write-resync branch can't be made pure
  // without rendering the hook; it's covered at the gateway/e2e layer instead.
  const mutate = useCallback(
    async (method: "PUT" | "DELETE", path: string, body?: unknown): Promise<void> => {
      if (!canFetch || tenantId === null || query.data?.resource !== resource || !query.data.view?.canEdit) return;
      setSaving(true);
      setMutationError(null);
      try {
        const res = await fetch(path, {
          method,
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        });
        if (!res.ok) throw new Error(`tool permission save failed (${res.status})`);
        // Write the server's authoritative overlay straight through the cache.
        const next = (await res.json()) as ToolPermissionsView;
        if (currentResource.current === resource) writeThroughCache(() => ({ resource, view: next }));
      } catch (err: unknown) {
        if (currentResource.current !== resource) return;
        flagMutationError(err instanceof Error ? err.message : String(err));
        // Resync from the server so a rejected write never leaves phantom state.
        await refetch().catch(() => {});
      } finally {
        setSaving(false);
      }
    },
    [canFetch, tenantId, resource, query.data, refetch, writeThroughCache, flagMutationError],
  );

  const setPermission = useCallback(
    (toolSlug: string | null, permission: ToolPermission): Promise<void> =>
      mutate("PUT", toolPermissionsBasePath(tenantId ?? "", slug), { toolSlug, permission }),
    [mutate, tenantId, slug],
  );

  const clearPermission = useCallback(
    (toolSlug: string): Promise<void> =>
      mutate("DELETE", `${toolPermissionsBasePath(tenantId ?? "", slug)}/${encodeURIComponent(toolSlug)}`),
    [mutate, tenantId, slug],
  );

  const resetAll = useCallback(
    (): Promise<void> => mutate("DELETE", toolPermissionsBasePath(tenantId ?? "", slug)),
    [mutate, tenantId, slug],
  );

  return {
    status: canFetch && query.data?.resource !== resource && query.status !== "error" ? "loading" : query.status,
    data: canFetch && query.data?.resource === resource ? query.data.view : null,
    error: mutationError ?? query.error,
    saving,
    setPermission,
    clearPermission,
    resetAll,
  };
};
